/**
 * A-04 卸一覧・接続（FR-05〜09・D-18）。
 *
 * **接続した卸しか出ない**＝招待制。「＋招待コードで卸を追加」だけが増やす手段。
 * エラー文言は**サーバが返したものをそのまま出す**（06 §2.3 の文言表が正）＝
 * 画面で書き直すと、卸名を出さない配慮（＝総当たりで取引関係を推測させない）が崩れる。
 *
 * ★**M1.8（D-96⑥）で絞り込み役を返上した**＝この画面は「接続の一覧（見るだけ）＋招待コードの
 *   入力」に専任する。以前は卸を押すと表示が絞り込まれたが、統合カタログでは絞り込み自体が
 *   要らなくなった（検索と発送元バッジが代替）。**URL（`/switch`）は変えない**＝リッチメニュー
 *   右下の着地点・招待リンク `?invite=` の着地点で、変えると配布済みの導線が壊れる。
 * ★**M1.9（D-98①）＝下部タブ「新規卸追加」へ昇格**。見出しもタブと同じ語にし、
 *   **招待コードの入力を先頭に**置く（タブ名が指す主目的を先に。接続済み一覧はその下）。
 * ★**M1.9b（Design 忠実化）＝接続済みを `.sup-card` に**（締め時刻・便・取扱分類・掛率）。
 *   足したのは**すでに取っているデータの読み替えだけ**＝便マスタとカタログは `queryKey` を
 *   カタログ画面と共有するので、この画面のために往復が増えることはない。
 */

import type { ConnectedTenant, PartnerBin, PartnerCatalog, partnerApi } from "@kenzai/api-client";
import { Badge, ErrorNote } from "@kenzai/ui";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Ico } from "./AddressBook";
import { PageTitle } from "../Shell";
import { useIsDesktop } from "../useIsDesktop";

type Client = ReturnType<typeof partnerApi>;

/** 見た目の正規化（大文字化・空白/ハイフン除去）。判定はサーバ側が正（03 §3）。 */
function normalizeCode(raw: string): string {
  return raw.replace(/[\s-]/g, "").toUpperCase().slice(0, 8);
}

/**
 * 便の要旨（Design `.sup-meta` の前半）。
 * Design は卸に締め時刻が1つだけの前提だが、実装の締め時刻は**便ごと**にある。
 * 全便が同じ時刻なら Design と同じ「10:00締め・午前便／午後便」、違うなら便ごとに時刻を添える
 * （まとめて1つの時刻を名乗ると、遅い便の締めに間に合うと誤解させる）。
 */
function binSummary(bins: PartnerBin[]): string | null {
  if (bins.length === 0) return null;
  // サーバは "10:00:00"（秒つき）で返す。締め時刻に秒の意味は無いので落とす。
  const hhmm = (t: string) => t.slice(0, 5);
  const times = new Set(bins.map((b) => hhmm(b.cutoff_time)));
  if (times.size === 1) return `${hhmm(bins[0].cutoff_time)}締め・${bins.map((b) => b.name).join("／")}`;
  return bins.map((b) => `${b.name} ${hhmm(b.cutoff_time)}締め`).join("／");
}

/** 締め時刻だけ（PC の表の1列）。**便ごとに違うなら1つを代表させない**＝「便ごと」と書く。 */
function cutoffText(bins: PartnerBin[]): string {
  if (bins.length === 0) return "—";
  const times = new Set(bins.map((b) => b.cutoff_time.slice(0, 5)));
  return times.size === 1 ? [...times][0] : "便ごと";
}

/** 配送便の名前（PC の表の1列）。 */
function binNamesText(bins: PartnerBin[]): string {
  return bins.length === 0 ? "—" : bins.map((b) => b.name).join("／");
}

/** 取扱分類（Design `.sup-fam` チップ）＝カタログの `category`。未設定は「その他」に畳む。 */
function categoriesOf(catalogs: PartnerCatalog[]): string[] {
  const seen = new Set<string>();
  for (const c of catalogs) for (const p of c.items) seen.add(p.category ?? "その他");
  return [...seen];
}

/**
 * 掛率バッジ（Design `.sup-kake`）。
 * **卸の中で掛率が1つに定まるときだけ出す**＝商品ごとに違う卸で1つの率を名乗ると嘘になる。
 * 個別単価・定価そのままの商品は `kake_percent` が null＝率の判定には数えない。
 */
function singleKakePercent(catalogs: PartnerCatalog[]): number | null {
  const rates = new Set<number>();
  for (const c of catalogs) {
    for (const p of c.items) if (p.kake_percent !== null) rates.add(p.kake_percent);
  }
  return rates.size === 1 ? [...rates][0] : null;
}

export function SwitchTenant({
  client,
  connections,
  standalone = false,
  prefill = "",
}: {
  client: Client;
  connections: ConnectedTenant[];
  /** 接続0本のとき＝タブの外に単独で出す（App から呼ばれる）。 */
  standalone?: boolean;
  /** 招待リンク（`?invite=`）で渡ってきたコード＝入力欄の初期値（06 §2.2）。 */
  prefill?: string;
}) {
  const qc = useQueryClient();
  // ★初期値だけ（以後は利用者の入力が正）。正規化はサーバもやるので、ここは見た目を整えるだけ。
  const nav = useNavigate();
  const [code, setCode] = useState(() => normalizeCode(prefill));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState<string | null>(null);
  // PC は Design の表・スマホはカード（`tenant-{id}` を二重に出さないため排他）
  const isDesktop = useIsDesktop();

  // 接続済みカードの中身。停止中の卸は API が 403 を返す（03 §8）＝引きに行かない。
  const usable = connections.filter((c) => c.tenant_status !== "suspended");
  const binQueries = useQueries({
    queries: usable.map((c) => ({ queryKey: ["bins", c.tenant_id], queryFn: () => client.bins(c.tenant_id) })),
  });
  const catalogQueries = useQueries({
    queries: usable.map((c) => ({ queryKey: ["catalog", c.tenant_id], queryFn: () => client.catalog(c.tenant_id) })),
  });
  const detailOf = (tenantId: number) => {
    const i = usable.findIndex((c) => c.tenant_id === tenantId);
    if (i < 0) return null;
    const catalogs = catalogQueries[i]?.data;
    const binList = binQueries[i]?.data ?? [];
    return {
      binList,
      bins: binSummary(binList),
      categories: catalogs ? categoriesOf(catalogs) : [],
      kake: catalogs ? singleKakePercent(catalogs) : null,
    };
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setAdded(null);
    try {
      const t = await client.redeem(code);
      setAdded(t.tenant_name);
      setCode("");
      // ★**URL から使用済みの招待コードを消す**（D-78）。消さないと、接続が増えて画面が
      //   差し替わったときに `?invite=` が残っていて**使用済みコードが再充填された招待画面**
      //   に着地する（＝「押したのに何も起きていない」ように見える。stg 一巡 F4 の実体）。
      //   ※`liffAuth.cleanUrl()` は触らない＝あちらは LINE ログインの往復でコードを保つ役割で、
      //     消してよいのは **redeem に成功した1回だけ**。
      if (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("invite")) {
        const url = new URL(window.location.href);
        url.searchParams.delete("invite");
        window.history.replaceState({}, "", url.toString());
      }
      // 接続直後の着地＝A-09（卸名の確認 → 連絡先の登録〔スキップ可〕→ 注文へ）
      //
      // ★**いま接続した卸を `state` で明示的に渡す**（D-96⑤・M1.8）。
      //   以前はここで絞り込みの値を書き（`onSwitch`）、A-09 がその値から卸を決めていた＝
      //   **絞り込みを撤去すると A-09 が「接続の先頭の卸」を名乗る**。つまり2社目の招待コードを
      //   入れた職人に**1社目の卸名で「◯◯とつながりました」と表示し、氏名も1社目の
      //   プロフィールへ保存する**（→新しい卸は氏名 null のまま＝チェックアウトの氏名ゲートで
      //   詰まる）という壊れ方をする。**この1行が唯一の受け渡し経路**なので消さないこと。
      //
      // ★**行き先を先に決めてから接続一覧を取り直す**（2026-07-31 修正）。
      //   逆順（invalidate を await してから nav）だと、**初回接続（0→1本）で welcome に
      //   着地しない**：await の間に一覧が 1 本になり、App が「接続0本の招待画面」から
      //   `<Routes>` の枝へ切り替わって、この画面ごと unmount される。そこで初めて
      //   `<Route path="/">` の `<Navigate to="/catalog" replace/>` が走り、
      //   遅れて呼ばれる nav("/welcome") と競って**カタログが勝つ**。
      //   ＝**A-09 が一番効いてほしい「はじめての接続」でだけ出ない**という壊れ方だった
      //   （まさに F4/U12 が指していた場面）。撮影スクリプトが実機で踏んで見つかった。
      //   先に nav しておけば、`<Routes>` がマウントした時点の location が /welcome になる。
      nav("/welcome", { state: { tenantId: t.tenant_id } });
      await qc.invalidateQueries({ queryKey: ["connections"] });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-4)" }}>
      {standalone ? (
        <div>
          <h1 style={{ fontSize: "var(--kz-font-size-xl)", margin: 0 }}>建材オーダー</h1>
          <p style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>
            {/* LC-06：接続オンボーディングのコピーは「招待制・無料・アプリ不要」 */}
            お取引先の建材卸売業者から届いた招待コードを入力すると、注文できるようになります。
          </p>
        </div>
      ) : (
        <div>
          <PageTitle>新規卸追加</PageTitle>
          <p style={{ margin: 0, fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
            お取引先の卸から届いた<strong>招待コード</strong>を入力すると、その卸の商品が
            「注文する」に追加されます（招待制＝コードのない卸は表示されません）。
          </p>
        </div>
      )}

      <div className="iv-card">
        <div className="iv-h">
          <Ico name="plusc" size={21} />
          招待コードで卸を追加
        </div>
        <form onSubmit={submit} noValidate data-testid="invite-form">
          <ErrorNote error={error} />
          {added && (
            <p
              role="status"
              style={{
                background: "var(--kz-color-primary-weak)",
                color: "var(--kz-color-primary)",
                padding: "var(--kz-space-3)",
                borderRadius: "var(--kz-radius-sm)",
                fontSize: "var(--kz-font-size-sm)",
              }}
            >
              {/* 06 §2.1 の歓迎メッセージと同じ趣旨＝**トーク注文へ誘導しない**（D-39） */}
              {added} とつながりました。「注文する」から、写真つきのカタログで選ぶだけです。
            </p>
          )}
          <label style={{ display: "block" }}>
            <span style={{ display: "block", fontSize: "var(--kz-font-size-sm)", fontWeight: 600, marginBottom: 4 }}>
              招待コード（8桁）
            </span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="invite-code"
              className="iv-input"
              // 現場でメモを見ながら打つ＝**大文字化と桁の見やすさ**を画面で助ける。
              // 正規化（小文字・空白・ハイフン）はサーバもやる＝ここは見た目の話。
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              inputMode="text"
              maxLength={8}
              placeholder="例）K7PQ2M4T"
              required
              style={{ minHeight: 56, font: "inherit", fontVariantNumeric: "tabular-nums" }}
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="invite-submit"
            className="kz-btn kz-btn--accent kz-pressable"
            style={{
              width: "100%",
              minHeight: 56,
              marginTop: "var(--kz-space-3)",
              fontSize: "var(--kz-font-size-lg)",
              fontWeight: 700,
              cursor: busy ? "wait" : "pointer",
            }}
          >
            {busy ? "確認中…" : "接続する"}
          </button>
        </form>
      </div>

      {connections.length > 0 && (
        <section>
          <div className="ck-lbl">お取引中の卸（{connections.length}社接続中）</div>
          <p className="oa-lead">
            {/* ★M1.8（D-96⑥）＝**押す場所ではなくなった**。商品はすべての卸のぶんが「注文する」に
                並んでいて（発送元が1品ずつ出る）、ここは「いまどこと取引できるか」の確認。
                押せる見た目のまま何も起きないボタンを残すより、素直に一覧にする。 */}
            「注文する」には、ここに並ぶ卸の商品がまとめて表示されます（商品ごとに発送元が出ます）。
          </p>

          {/* ★PC＝Design の表（締め・便・掛率を並べて見比べる）。スマホはカード（`.sup-card`）＝
              **排他レンダリング**（`tenant-{id}` を二重に出さない）。
              Design にある「未接続の卸」の行と「累計発注額」の列は作らない：前者は招待制の原則
              （総当たりで取引関係を推測させない・06 §2.3）に反し、後者は一覧 API が1ページ分しか
              返さないので**正確な累計を出せない**（部分合計を「累計」と書くと嘘になる）。 */}
          {isDesktop ? (
            <div className="kp-tblwrap">
              <table className="kp-tbl">
                <thead>
                  <tr>
                    <th>お取引先</th>
                    <th>ご利用</th>
                    <th className="num">掛け率</th>
                    <th>締め時刻</th>
                    <th>配送便</th>
                    <th>取扱</th>
                    <th aria-label="操作" />
                  </tr>
                </thead>
                <tbody>
                  {connections.map((c) => {
                    const d = detailOf(c.tenant_id);
                    const suspended = c.tenant_status === "suspended";
                    return (
                      <tr key={c.tenant_id} data-testid={`tenant-${c.tenant_id}`}>
                        <td>
                          <b>{c.tenant_name}</b>
                        </td>
                        <td>
                          {suspended ? <Badge tone="danger">利用できません</Badge> : <Badge tone="ok">接続済み</Badge>}
                        </td>
                        <td className="num">
                          {d?.kake !== null && d?.kake !== undefined ? `${d.kake}%` : "—"}
                        </td>
                        <td className="kz-num">{suspended ? "—" : cutoffText(d?.binList ?? [])}</td>
                        <td>{suspended ? "—" : binNamesText(d?.binList ?? [])}</td>
                        <td>
                          {(d?.categories.length ?? 0) > 0 ? (
                            <span className="kp-summary">{d?.categories.join("・")}</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <span className="kp-acts">
                            {!suspended && (
                              <button type="button" className="kp-act" onClick={() => nav("/catalog")}>
                                この卸の商品を見る
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            connections.map((c) => {
            const d = detailOf(c.tenant_id);
            return (
              <div key={c.tenant_id} data-testid={`tenant-${c.tenant_id}`} className="sup-card">
                <div className="sup-h">
                  <Ico name="store" />
                  <b style={{ minWidth: 0, overflowWrap: "anywhere" }}>{c.tenant_name}</b>
                  {c.tenant_status === "suspended" ? (
                    <span style={{ marginLeft: "auto" }}>
                      <Badge tone="danger">利用できません</Badge>
                    </span>
                  ) : (
                    d?.kake !== null &&
                    d?.kake !== undefined && <span className="sup-kake kz-num">掛率 {d.kake}%</span>
                  )}
                </div>
                {(d?.bins || (d?.categories.length ?? 0) > 0) && (
                  <div className="sup-meta">
                    {d?.bins}
                    {d?.bins && (d?.categories.length ?? 0) > 0 && <span className="sup-sep">｜</span>}
                    {(d?.categories.length ?? 0) > 0 && `取扱 ${d?.categories.length}分類`}
                  </div>
                )}
                {(d?.categories.length ?? 0) > 0 && (
                  <div className="sup-fams">
                    {d?.categories.map((name) => (
                      <span key={name} className="sup-fam">
                        {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
            })
          )}

          {/* 接続の解除は卸側の操作（D-67＝卸コンソール C-20 だけが持つ）＝
              職人の画面に押せないボタンを置かず、どうすればよいかを1行で言う。 */}
          <p className="oa-foot">接続の解除は、お取引先にお申し出ください。</p>
        </section>
      )}
      {/* ★接続の**前**に読める導線（T-D29・09 §K.3）＝ここで初めて個人情報を預ける操作
          （接続）をするので、その手前に置く。
          ★**standalone のときだけ出す**（D-97②・M1.8）＝Shell の中では本文の末尾に同じリンクが
            常設されている（`privacy-link`）ので、両方出すと同じページへのリンクが1画面に2本
            並ぶ。接続0本の初回の職人は Shell の外に出るので、そこでは**ここが唯一の導線**。 */}
      {standalone && (
        <p style={{ textAlign: "center", margin: 0 }}>
          <a
            href="/privacy"
            data-testid="privacy-link-invite"
            style={{ color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}
          >
            プライバシーポリシー
          </a>
        </p>
      )}
    </div>
  );
}
